import { useState } from "react";
import { Plus, Save, Trash2, Check, Copy, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import {
  useCorrelationConfigs,
  useCreateCorrelationConfig,
  useUpdateCorrelationConfig,
  useActivateCorrelationConfig,
  useDeleteCorrelationConfig,
  useAminoAgePreview,
  PREVIEW_PAGE_SIZE,
  type CorrelationConfig,
  type CorrelationMatrix,
  type CategoryWeights,
  type PreviewUser,
} from "@/hooks/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";

// NOTE: These must match CATEGORY_IDS and DEFAULT_CORRELATION_MATRIX in
// backend/src/modules/amino-age/calculate.ts
const CATEGORY_IDS = [
  "blodprover", "vo2maks", "sone1-3", "sone4-5", "skritt",
  "sovnlengde", "sovnkvalitet", "sovnkonsistens", "hvilepuls", "hrv",
] as const;

const CATEGORY_LABELS: Record<string, string> = {
  blodprover: "Blod",
  vo2maks: "VO2",
  "sone1-3": "S1-3",
  "sone4-5": "S4-5",
  skritt: "Skritt",
  sovnlengde: "SøvnL",
  sovnkvalitet: "SøvnK",
  hvilepuls: "Hvile",
  hrv: "HRV",
  sovnkonsistens: "SøvnR",
};

const DEFAULT_CORRELATIONS: CorrelationMatrix = {
  blodprover:     { blodprover: 1.00, vo2maks: 0.15, "sone1-3": 0.05, "sone4-5": 0.10, skritt: 0.05, sovnlengde: 0.05, sovnkvalitet: 0.05, hvilepuls: 0.10, hrv: 0.10, sovnkonsistens: 0.10 },
  vo2maks:        { blodprover: 0.15, vo2maks: 1.00, "sone1-3": 0.30, "sone4-5": 0.55, skritt: 0.20, sovnlengde: 0.05, sovnkvalitet: 0.10, hvilepuls: 0.60, hrv: 0.65, sovnkonsistens: 0.05 },
  "sone1-3":      { blodprover: 0.05, vo2maks: 0.30, "sone1-3": 1.00, "sone4-5": 0.35, skritt: 0.40, sovnlengde: 0.05, sovnkvalitet: 0.05, hvilepuls: 0.15, hrv: 0.15, sovnkonsistens: 0.05 },
  "sone4-5":      { blodprover: 0.10, vo2maks: 0.55, "sone1-3": 0.35, "sone4-5": 1.00, skritt: 0.15, sovnlengde: 0.05, sovnkvalitet: 0.10, hvilepuls: 0.35, hrv: 0.40, sovnkonsistens: 0.05 },
  skritt:         { blodprover: 0.05, vo2maks: 0.20, "sone1-3": 0.40, "sone4-5": 0.15, skritt: 1.00, sovnlengde: 0.10, sovnkvalitet: 0.05, hvilepuls: 0.10, hrv: 0.10, sovnkonsistens: 0.05 },
  sovnlengde:     { blodprover: 0.05, vo2maks: 0.05, "sone1-3": 0.05, "sone4-5": 0.05, skritt: 0.10, sovnlengde: 1.00, sovnkvalitet: 0.30, hvilepuls: 0.10, hrv: 0.15, sovnkonsistens: 0.25 },
  sovnkvalitet:   { blodprover: 0.05, vo2maks: 0.10, "sone1-3": 0.05, "sone4-5": 0.10, skritt: 0.05, sovnlengde: 0.30, sovnkvalitet: 1.00, hvilepuls: 0.10, hrv: 0.20, sovnkonsistens: 0.35 },
  hvilepuls:      { blodprover: 0.10, vo2maks: 0.60, "sone1-3": 0.15, "sone4-5": 0.35, skritt: 0.10, sovnlengde: 0.10, sovnkvalitet: 0.10, hvilepuls: 1.00, hrv: 0.70, sovnkonsistens: 0.10 },
  hrv:            { blodprover: 0.10, vo2maks: 0.65, "sone1-3": 0.15, "sone4-5": 0.40, skritt: 0.10, sovnlengde: 0.15, sovnkvalitet: 0.20, hvilepuls: 0.70, hrv: 1.00, sovnkonsistens: 0.15 },
  sovnkonsistens: { blodprover: 0.10, vo2maks: 0.05, "sone1-3": 0.05, "sone4-5": 0.05, skritt: 0.05, sovnlengde: 0.25, sovnkvalitet: 0.35, hvilepuls: 0.10, hrv: 0.15, sovnkonsistens: 1.00 },
};

/** Compute discount factors from a correlation matrix: discount = 1/(1 + Σρ) */
function computeDiscounts(matrix: CorrelationMatrix): Record<string, number> {
  const result: Record<string, number> = {};
  for (const id of CATEGORY_IDS) {
    let redundancy = 0;
    for (const other of CATEGORY_IDS) {
      if (other === id) continue;
      const rho = matrix[id]?.[other] ?? matrix[other]?.[id] ?? 0;
      redundancy += rho;
    }
    result[id] = 1 / (1 + redundancy);
  }
  return result;
}

/** Compute discount factors for a specific user's available categories only */
function computeUserDiscounts(availableIds: string[], matrix: CorrelationMatrix): Record<string, number> {
  const result: Record<string, number> = {};
  for (const id of availableIds) {
    let redundancy = 0;
    for (const other of availableIds) {
      if (other === id) continue;
      const rho = matrix[id]?.[other] ?? matrix[other]?.[id] ?? 0;
      redundancy += rho;
    }
    result[id] = 1 / (1 + redundancy);
  }
  return result;
}

function colorForValue(v: number): string {
  if (v >= 0.6) return "bg-admin-danger-500/30 text-admin-danger-300";
  if (v >= 0.4) return "bg-orange-500/25 text-orange-300";
  if (v >= 0.2) return "bg-admin-caution-500/20 text-admin-caution-300";
  if (v > 0.05) return "bg-admin-info-500/15 text-admin-info-300";
  return "text-admin-text-subtle";
}

function discountColor(d: number): string {
  if (d >= 0.95) return "text-admin-success-400";
  if (d >= 0.85) return "text-admin-info-400";
  if (d >= 0.75) return "text-admin-caution-400";
  return "text-orange-400";
}

// ── Page Component ──────────────────────────────────────────────────────────

export function CorrelationConfigsPage() {
  const { data: configs, isLoading } = useCorrelationConfigs();
  const createConfig = useCreateCorrelationConfig();
  const updateConfig = useUpdateCorrelationConfig();
  const activateConfig = useActivateCorrelationConfig();
  const deleteConfig = useDeleteCorrelationConfig();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [previewConfigId, setPreviewConfigId] = useState<string | undefined>(undefined);
  const [previewRequested, setPreviewRequested] = useState(false);

  const handleCreate = async () => {
    const config = await createConfig.mutateAsync({
      name: `Ny konfig ${new Date().toISOString().slice(0, 10)}`,
      active: false,
      correlations: structuredClone(DEFAULT_CORRELATIONS),
      weights: null,
    });
    setEditingId(config.id);
    toast.success("Konfigurasjon opprettet");
  };

  const handleDuplicate = async (source: CorrelationConfig) => {
    const config = await createConfig.mutateAsync({
      name: `${source.name} (kopi)`,
      active: false,
      correlations: structuredClone(source.correlations),
      weights: source.weights ? { ...source.weights } : null,
    });
    setEditingId(config.id);
    toast.success("Konfigurasjon duplisert");
  };

  const handleActivate = async (id: string) => {
    await activateConfig.mutateAsync(id);
    toast.success("Konfigurasjon aktivert");
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Slett denne konfigurasjonen?")) return;
    await deleteConfig.mutateAsync(id);
    if (editingId === id) setEditingId(null);
    toast.success("Konfigurasjon slettet");
  };

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-admin-text-subtle" /></div>;
  }

  const sorted = [...(configs ?? [])].sort((a, b) => {
    if (a.active !== b.active) return a.active ? -1 : 1;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-admin-text-primary">Korrelasjonsmatriser</h2>
          <p className="text-sm text-admin-text-subtle">
            Administrer korrelasjonsmatriser og vekter for Amino Age-beregning.
            Den aktive konfigurasjonen brukes for alle nye beregninger.
          </p>
        </div>
        <Button onClick={handleCreate} size="sm" disabled={createConfig.isPending}>
          <Plus className="h-4 w-4" />
          Ny konfigurasjon
        </Button>
      </div>

      <div className="space-y-4">
        {sorted.map((config) => (
          <ConfigCard
            key={config.id}
            config={config}
            isEditing={editingId === config.id}
            onEdit={() => setEditingId(editingId === config.id ? null : config.id)}
            onActivate={() => handleActivate(config.id)}
            onDuplicate={() => handleDuplicate(config)}
            onDelete={() => handleDelete(config.id)}
            onPreview={() => {
              setPreviewConfigId(config.id);
              setPreviewRequested(true);
            }}
            onSave={async (data) => {
              await updateConfig.mutateAsync({ id: config.id, data });
              toast.success("Konfigurasjon lagret");
            }}
            isSaving={updateConfig.isPending}
          />
        ))}
      </div>

      {/* User preview table */}
      <UserPreviewTable
        configId={previewConfigId}
        configs={sorted}
        onConfigChange={setPreviewConfigId}
        enabled={previewRequested}
        onRun={() => setPreviewRequested(true)}
      />
    </div>
  );
}

// ── Config Card ─────────────────────────────────────────────────────────────

function ConfigCard({
  config,
  isEditing,
  onEdit,
  onActivate,
  onDuplicate,
  onDelete,
  onPreview,
  onSave,
  isSaving,
}: {
  config: CorrelationConfig;
  isEditing: boolean;
  onEdit: () => void;
  onActivate: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onPreview: () => void;
  onSave: (data: Partial<CorrelationConfig>) => Promise<void>;
  isSaving: boolean;
}) {
  // Reset local form state when the config changes externally (e.g. after save)
  const configVersion = config.updatedAt;
  const [name, setName] = useState(config.name);
  const [matrix, setMatrix] = useState<CorrelationMatrix>(() => structuredClone(config.correlations));
  const [weights, setWeights] = useState<CategoryWeights | null>(config.weights);
  const [showWeights, setShowWeights] = useState(!!config.weights);
  const [lastVersion, setLastVersion] = useState(configVersion);
  if (configVersion !== lastVersion) {
    setLastVersion(configVersion);
    setName(config.name);
    setMatrix(structuredClone(config.correlations));
    setWeights(config.weights);
    setShowWeights(!!config.weights);
  }

  const discounts = computeDiscounts(matrix);

  const handleSetAllToOne = () => {
    setMatrix((prev) => {
      const next = structuredClone(prev);
      for (const row of CATEGORY_IDS) {
        for (const col of CATEGORY_IDS) {
          if (!next[row]) next[row] = {};
          next[row][col] = row === col ? 1.0 : 1.0;
        }
      }
      return next;
    });
  };

  const handleCellChange = (row: string, col: string, value: string) => {
    const num = parseFloat(value);
    if (isNaN(num) || num < 0 || num > 1) return;
    const rounded = Math.round(num * 100) / 100;
    setMatrix((prev) => {
      const next = structuredClone(prev);
      if (!next[row]) next[row] = {};
      if (!next[col]) next[col] = {};
      next[row][col] = rounded;
      next[col][row] = rounded; // keep symmetric
      return next;
    });
  };

  const handleWeightChange = (id: string, value: string) => {
    const num = parseFloat(value);
    if (isNaN(num) || num < 0) return;
    const rounded = Math.round(num * 100) / 100;
    setWeights((prev) => ({ ...prev, [id]: rounded }));
  };

  const handleSave = () => {
    onSave({
      name,
      correlations: matrix,
      weights: showWeights ? weights : null,
    });
  };

  // Reset local state when toggling edit off
  const handleToggleEdit = () => {
    if (isEditing) {
      setName(config.name);
      setMatrix(structuredClone(config.correlations));
      setWeights(config.weights);
      setShowWeights(!!config.weights);
    }
    onEdit();
  };

  return (
    <Card className={config.active ? "border-admin-primary/50" : ""}>
      <CardContent className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {config.active && (
              <span className="inline-flex items-center gap-1 rounded-full bg-admin-primary/15 px-2.5 py-0.5 text-xs font-medium text-admin-primary">
                <Check className="h-3 w-3" />
                Aktiv
              </span>
            )}
            {isEditing ? (
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="max-w-xs text-sm font-semibold"
              />
            ) : (
              <h3 className="font-semibold text-admin-text-primary">{config.name}</h3>
            )}
            <span className="text-xs text-admin-text-subtle">
              {new Date(config.createdAt).toLocaleDateString("nb-NO")}
            </span>
          </div>
          <div className="flex items-center gap-1">
            {!config.active && (
              <Button variant="ghost" size="sm" onClick={onActivate} className="text-xs h-7 text-admin-primary">
                Aktiver
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onPreview} className="text-xs h-7 text-admin-primary">
              Forhåndsvis
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onDuplicate()} className="text-xs h-7">
              <Copy className="h-3 w-3" />
              Dupliser
            </Button>
            <Button variant="ghost" size="sm" onClick={handleToggleEdit} className="text-xs h-7">
              {isEditing ? "Avbryt" : "Rediger"}
            </Button>
            {!config.active && (
              <Button variant="ghost" size="icon" onClick={onDelete} className="text-admin-negative hover:text-admin-negative h-7 w-7">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>

        {/* Matrix (always shown) */}
        <div className="overflow-x-auto">
          <CorrelationMatrixView
            matrix={matrix}
            discounts={discounts}
            editable={isEditing}
            onCellChange={handleCellChange}
          />
          {isEditing && (
            <div className="mt-2">
              <Button variant="ghost" size="sm" onClick={handleSetAllToOne} className="text-xs h-7">
                Sett alle til 1
              </Button>
            </div>
          )}
        </div>

        {/* Weights (optional, shown in edit mode or when present) */}
        {isEditing && (
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={showWeights}
                onChange={(e) => {
                  setShowWeights(e.target.checked);
                  if (e.target.checked && !weights) {
                    const defaultWeights: CategoryWeights = {};
                    for (const id of CATEGORY_IDS) defaultWeights[id] = 1.0;
                    setWeights(defaultWeights);
                  }
                }}
                className="rounded accent-admin-primary"
              />
              <span className="text-admin-text-secondary">Egendefinerte vekter per kategori</span>
            </label>
            {showWeights && weights && (
              <WeightsEditor weights={weights} onChange={handleWeightChange} />
            )}
          </div>
        )}

        {!isEditing && config.weights && (
          <div className="space-y-1">
            <p className="text-xs font-medium text-admin-text-subtle">Vekter</p>
            <div className="flex flex-wrap gap-2">
              {CATEGORY_IDS.map((id) => (
                <span key={id} className="text-xs bg-admin-slate-hover rounded px-2 py-0.5">
                  {CATEGORY_LABELS[id]}: {config.weights?.[id] ?? 1.0}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Save button */}
        {isEditing && (
          <div className="flex justify-end pt-2">
            <Button size="sm" onClick={handleSave} disabled={isSaving}>
              <Save className="h-3 w-3" />
              {isSaving ? "Lagrer..." : "Lagre"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Matrix View ─────────────────────────────────────────────────────────────

function CorrelationMatrixView({
  matrix,
  discounts,
  editable,
  onCellChange,
}: {
  matrix: CorrelationMatrix;
  discounts: Record<string, number>;
  editable: boolean;
  onCellChange: (row: string, col: string, value: string) => void;
}) {
  return (
    <table className="text-xs border-collapse">
      <thead>
        <tr>
          <th className="p-1 text-left text-admin-text-subtle font-medium w-16" />
          {CATEGORY_IDS.map((id) => (
            <th key={id} className="p-1 text-center text-admin-text-subtle font-medium w-14">
              {CATEGORY_LABELS[id]}
            </th>
          ))}
          <th className="p-1 text-center text-admin-text-subtle font-medium w-16 border-l border-admin-card-border">
            Rabatt
          </th>
        </tr>
      </thead>
      <tbody>
        {CATEGORY_IDS.map((rowId, ri) => (
          <tr key={rowId}>
            <td className="p-1 text-admin-text-subtle font-medium">{CATEGORY_LABELS[rowId]}</td>
            {CATEGORY_IDS.map((colId, ci) => {
              const val = matrix[rowId]?.[colId] ?? 0;
              const isDiagonal = ri === ci;
              const isUpperTriangle = ci > ri;

              if (isDiagonal) {
                return (
                  <td key={colId} className="p-1 text-center bg-admin-card-border/30 text-admin-text-subtle">
                    1.00
                  </td>
                );
              }

              // Lower triangle mirrors upper — show read-only
              if (!isUpperTriangle) {
                return (
                  <td key={colId} className={`p-1 text-center ${colorForValue(val)}`}>
                    {val.toFixed(2)}
                  </td>
                );
              }

              // Upper triangle — editable
              if (editable) {
                return (
                  <td key={colId} className="p-0.5">
                    <input
                      type="number"
                      min="0"
                      max="1"
                      step="0.1"
                      value={val}
                      onChange={(e) => onCellChange(rowId, colId, e.target.value)}
                      className={`w-14 text-center text-xs rounded px-1 py-0.5 bg-admin-card border border-admin-card-border focus:border-admin-primary focus:outline-none ${colorForValue(val)}`}
                    />
                  </td>
                );
              }

              return (
                <td key={colId} className={`p-1 text-center ${colorForValue(val)}`}>
                  {val.toFixed(2)}
                </td>
              );
            })}
            <td className={`p-1 text-center font-mono font-medium border-l border-admin-card-border ${discountColor(discounts[rowId])}`}>
              {discounts[rowId].toFixed(3)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Weights Editor ──────────────────────────────────────────────────────────

function WeightsEditor({
  weights,
  onChange,
}: {
  weights: CategoryWeights;
  onChange: (id: string, value: string) => void;
}) {
  return (
    <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2">
      {CATEGORY_IDS.map((id) => (
        <div key={id} className="space-y-0.5">
          <label className="block text-xs text-admin-text-subtle">{CATEGORY_LABELS[id]}</label>
          <input
            type="number"
            min="0"
            max="5"
            step="0.1"
            value={weights[id] ?? 1.0}
            onChange={(e) => onChange(id, e.target.value)}
            className="w-full text-center text-xs rounded px-1 py-1 bg-admin-card border border-admin-card-border focus:border-admin-primary focus:outline-none"
          />
        </div>
      ))}
    </div>
  );
}

// ── User Preview Table ──────────────────────────────────────────────────────

function deltaColor(v: number | null): string {
  if (v === null) return "text-admin-text-subtle";
  if (v <= -2) return "text-admin-success-400";
  if (v < 0) return "text-admin-success-300";
  if (v === 0) return "text-admin-text-secondary";
  if (v <= 2) return "text-orange-300";
  return "text-admin-danger-400";
}

function ageDiffColor(v: number | null): string {
  if (v === null) return "text-admin-text-subtle";
  if (v <= -8) return "text-admin-success-400 font-bold";
  if (v <= -3) return "text-admin-success-400";
  if (v < 0) return "text-admin-success-300";
  if (v === 0) return "text-admin-text-secondary";
  if (v <= 3) return "text-orange-300";
  if (v <= 8) return "text-admin-danger-400";
  return "text-admin-danger-400 font-bold";
}

function UserPreviewTable({
  configId,
  configs,
  onConfigChange,
  enabled,
  onRun,
}: {
  configId: string | undefined;
  configs: CorrelationConfig[];
  onConfigChange: (id: string | undefined) => void;
  enabled: boolean;
  onRun: () => void;
}) {
  // `searchInput` is what is being typed, `search` what has been sent — a
  // keystroke must not kick off a run of the pipeline.
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);

  const { data, isLoading, isFetching } = useAminoAgePreview({
    configId,
    offset: page * PREVIEW_PAGE_SIZE,
    search,
    enabled,
  });

  const applySearch = () => {
    setSearch(searchInput.trim());
    setPage(0);
    onRun();
  };

  const activeConfig = configId
    ? configs.find(c => c.id === configId)
    : configs.find(c => c.active);
  const matrix = activeConfig?.correlations ?? DEFAULT_CORRELATIONS;

  const users = data?.users ?? [];
  const total = data?.total ?? 0;
  const rangeStart = total === 0 ? 0 : page * PREVIEW_PAGE_SIZE + 1;
  const rangeEnd = Math.min((page + 1) * PREVIEW_PAGE_SIZE, total);
  const hasNextPage = rangeEnd < total;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-admin-text-primary">Brukerforhåndsvisning</h2>
          <p className="text-sm text-admin-text-subtle">
            Live Amino Age, beregnet én side om gangen.
            {data?.configName && <> Bruker: <span className="text-admin-primary">{data.configName}</span></>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={configId ?? ""}
            onChange={(e) => {
              onConfigChange(e.target.value || undefined);
              setPage(0);
            }}
            className="text-xs rounded px-2 py-1.5 bg-admin-card border border-admin-card-border focus:border-admin-primary focus:outline-none text-admin-text-primary"
          >
            <option value="">Aktiv konfigurasjon</option>
            {configs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}{c.active ? " (aktiv)" : ""}
              </option>
            ))}
          </select>
          <Button variant="ghost" size="sm" onClick={applySearch} className="text-xs h-7 text-admin-primary">
            {enabled ? "Oppdater" : "Kjør forhåndsvisning"}
          </Button>
          {enabled && (isLoading || isFetching) && <Loader2 className="h-4 w-4 animate-spin text-admin-text-subtle" />}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-admin-text-subtle" />
          <input
            type="text"
            placeholder="Søk navn (f.eks. aksel carl helene), Enter for å søke..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") applySearch();
            }}
            className="w-full pl-8 pr-3 py-1.5 text-xs rounded bg-admin-card border border-admin-card-border focus:border-admin-primary focus:outline-none text-admin-text-primary placeholder:text-admin-text-subtle"
          />
        </div>
        {enabled && (
          <div className="flex items-center gap-2 text-xs text-admin-text-subtle whitespace-nowrap">
            <span>{total > 0 ? `${rangeStart}–${rangeEnd} av ${total}` : "0 treff"}</span>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7"
              disabled={page === 0 || isFetching}
              onClick={() => setPage((p) => p - 1)}
            >
              Forrige
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7"
              disabled={!hasNextPage || isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              Neste
            </Button>
          </div>
        )}
      </div>

      {users.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="border-b border-admin-card-border">
                <th className="p-1.5 text-left text-admin-text-subtle font-medium">Navn</th>
                <th className="p-1.5 text-center text-admin-text-subtle font-medium w-8">K</th>
                <th className="p-1.5 text-center text-admin-text-subtle font-medium w-10">Alder</th>
                <th className="p-1.5 text-center text-admin-text-subtle font-medium w-14">Amino</th>
                <th className="p-1.5 text-center text-admin-text-subtle font-medium w-12">Diff</th>
                <th className="p-1.5 text-center text-admin-text-subtle font-medium w-12" title="Sum av rå delta (uten korrelasjonsrabatt)">Σδ</th>
                <th className="p-1.5 text-center text-admin-text-subtle font-medium w-8">N</th>
                {CATEGORY_IDS.map((id) => (
                  <th key={id} className="p-1.5 text-center text-admin-text-subtle font-medium w-12">
                    {CATEGORY_LABELS[id]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const catMap = new Map(user.categories.map((c) => [c.id, c]));
                const availableIds = user.categories.filter(c => c.delta !== null).map(c => c.id);
                const userDiscounts = computeUserDiscounts(availableIds, matrix);
                const rawDeltaSum = user.categories.reduce((sum, c) => sum + (c.delta ?? 0), 0);
                return (
                  <tr key={user.authUserId} className="border-b border-admin-card-border/50 hover:bg-admin-slate-hover/50">
                    <td className="p-1.5 text-admin-text-primary font-medium whitespace-nowrap">{user.name}</td>
                    <td className="p-1.5 text-center text-admin-text-secondary">{user.gender === "male" ? "M" : "F"}</td>
                    <td className="p-1.5 text-center text-admin-text-secondary">{user.chronologicalAge}</td>
                    <td className="p-1.5 text-center font-mono font-medium text-admin-text-primary">
                      {user.aminoAge?.toFixed(1) ?? "—"}
                    </td>
                    <td className={`p-1.5 text-center font-mono font-medium ${ageDiffColor(user.ageDifference)}`}>
                      {user.ageDifference !== null ? `${user.ageDifference > 0 ? "+" : ""}${user.ageDifference.toFixed(1)}` : "—"}
                    </td>
                    <td className={`p-1.5 text-center font-mono font-medium ${ageDiffColor(rawDeltaSum)}`}
                      title="Sum av rå delta uten korrelasjonsrabatt">
                      {availableIds.length > 0 ? `${rawDeltaSum > 0 ? "+" : ""}${rawDeltaSum.toFixed(1)}` : "—"}
                    </td>
                    <td className="p-1.5 text-center text-admin-text-subtle">{user.availableCategories}</td>
                    {CATEGORY_IDS.map((catId) => {
                      const cat = catMap.get(catId);
                      const contribution = cat?.contribution;
                      const discount = userDiscounts[catId];
                      return (
                        <td key={catId} className={`p-1.5 text-center font-mono ${deltaColor(contribution ?? null)}`}
                          title={cat ? `raw: ${cat.rawValue?.toFixed(1) ?? "—"} ${cat.rawUnit}\ndelta (uavh.): ${cat.delta?.toFixed(2) ?? "—"}\nrabatt: ${discount?.toFixed(3) ?? "—"}\nbidrag: ${contribution?.toFixed(2) ?? "—"}` : "mangler"}>
                          <div>{contribution !== null && contribution !== undefined ? contribution.toFixed(1) : "—"}</div>
                          {cat?.delta != null && (
                            <div className={`text-[10px] font-normal ${deltaColor(cat.delta)}`}>
                              δ{cat.delta > 0 ? "+" : ""}{cat.delta.toFixed(1)}
                            </div>
                          )}
                          {cat?.rawValue != null && (
                            <div className="text-[10px] text-admin-text-subtle font-normal">{cat.rawValue.toFixed(1)}</div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!enabled && (
        <p className="text-sm text-admin-text-subtle text-center py-8">
          Forhåndsvisningen beregner Amino Age live per bruker og kjører derfor
          bare på forespørsel. Trykk «Kjør forhåndsvisning».
        </p>
      )}

      {enabled && !isLoading && users.length === 0 && (
        <p className="text-sm text-admin-text-subtle text-center py-8">
          {search ? "Ingen treff" : "Ingen brukere med tilstrekkelig data"}
        </p>
      )}
    </div>
  );
}
